import React, { useState } from 'react';
import { Plus, Search, Play, Pause } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useSchedules,
  useDeleteSchedule,
  useExecuteSchedule,
  usePauseSchedule,
  useResumeSchedule,
} from '@/hooks/reporting/useSchedules';
import ScheduleCard from '@/components/reports/cards/ScheduleCard';
import ConfirmDeleteModal from '@/components/reports/modals/ConfirmDeleteModal';
import EmptyState from '@/components/reports/common/EmptyState';
import type { ListSchedulesQuery } from '@/types/reporting-v2';

export default function SchedulesList() {
  const navigate = useNavigate();
  const deleteSchedule = useDeleteSchedule();
  const executeSchedule = useExecuteSchedule();
  const pauseSchedule = usePauseSchedule();
  const resumeSchedule = useResumeSchedule();

  const [query, setQuery] = useState<ListSchedulesQuery>({
    page: 1,
    limit: 12,
  });

  const { data, isLoading, refetch } = useSchedules(query);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused'>('all');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    scheduleId?: string;
    scheduleName?: string;
  }>({
    isOpen: false,
  });

  const handleSearch = () => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      search: searchTerm || undefined,
    }));
  };

  const handleStatusFilter = (status: 'all' | 'active' | 'paused') => {
    setFilterStatus(status);
    setQuery((prev) => ({
      ...prev,
      page: 1,
      is_active: status === 'all' ? undefined : status === 'active',
    }));
  };

  const handlePageChange = (page: number) => {
    setQuery((prev) => ({ ...prev, page }));
  };

  const handleExecute = async (scheduleId: string) => {
    await executeSchedule.mutateAsync({ scheduleId });
    refetch();
  };

  const handlePauseResume = async (scheduleId: string, isActive: boolean) => {
    if (isActive) {
      await pauseSchedule.mutateAsync(scheduleId);
    } else {
      await resumeSchedule.mutateAsync(scheduleId);
    }
    refetch();
  };

  const handleEdit = (scheduleId: string) => {
    navigate(`/admin/reporting/schedules/${scheduleId}/edit`);
  };

  const handleView = (scheduleId: string) => {
    navigate(`/admin/reporting/schedules/${scheduleId}`);
  };

  const handleDeleteClick = (scheduleId: string, scheduleName: string) => {
    setDeleteModal({
      isOpen: true,
      scheduleId,
      scheduleName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.scheduleId) {
      await deleteSchedule.mutateAsync(deleteModal.scheduleId);
      setDeleteModal({ isOpen: false });
      refetch();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedules</h1>
          <p className="text-sm text-gray-500 mt-1">
            Automate report generation with scheduled jobs
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/reporting/schedules/create')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Schedule
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="space-y-4">
          {/* Search */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search schedules by name..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Search
            </button>
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleStatusFilter('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => handleStatusFilter('paused')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'paused'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Paused
            </button>
          </div>
        </div>
      </div>

      {/* Schedules Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : data?.schedules && data.schedules.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.schedules.map((schedule) => {
              const scheduleId = (schedule as any)._id || schedule.schedule_id;
              return (
                <ScheduleCard
                  key={scheduleId}
                  schedule={schedule}
                  onExecute={() => handleExecute(scheduleId)}
                  onPause={() => handlePauseResume(scheduleId, true)}
                  onResume={() => handlePauseResume(scheduleId, false)}
                  onEdit={() => handleEdit(scheduleId)}
                  onDelete={() => handleDeleteClick(scheduleId, schedule.schedule_name)}
                />
              );
            })}
          </div>

          {/* Pagination */}
          {data.pagination && data.pagination.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {(data.pagination.page - 1) * data.pagination.limit + 1} to{' '}
                {Math.min(
                  data.pagination.page * data.pagination.limit,
                  data.pagination.total
                )}{' '}
                of {data.pagination.total} schedules
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(query.page! - 1)}
                  disabled={query.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(query.page! + 1)}
                  disabled={query.page === data.pagination.total_pages}
                  className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <EmptyState
            icon={Plus}
            title="No schedules found"
            description="Get started by creating your first automated report schedule"
            action={{
              label: 'Create Schedule',
              onClick: () => navigate('/admin/reporting/schedules/create'),
            }}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={handleDeleteConfirm}
        title="Delete Schedule"
        message="Are you sure you want to delete this schedule? This action cannot be undone."
        itemName={deleteModal.scheduleName}
        loading={deleteSchedule.isPending}
      />
    </div>
  );
}
